import type { CSSProperties } from 'react';
import type { LayerData } from '@apps/card/routes/CardCustomizer/components/Layer.types';
import { absolutePhotoUrl, applyRenditionIfMediaServiceUrl } from './mediaService';
import type { Rendition } from './mediaService';

/**
 * Colorway live-preview helpers.
 *
 * A colorway template carries, on the ThemeJson, a `colorways` object with an
 * `options` array of `{ id, values }` entries, each `values` an ordered
 * `string[]`. Layer color/src fields reference a `values` entry by index via a
 * `var(--N)` token; the first color value is the swatch color. FE preview
 * surfaces (gallery tile, PDP, editor
 * canvas) render those tokens by setting the selected colorway's values as
 * inline CSS custom properties (`--N`) on an ancestor element —
 * non-destructive, so the draft keeps its tokens for the backend to collapse
 * at render time. See `useColorways`.
 */

const VAR_RE = /^var\(\s*--(\d+)\s*\)$/;

/**
 * Composes the theme-search embeddings-index row id for a colorway variant.
 * The index (themes.json/themes.bin) keys each colorway variant as
 * `${themeId}_${colorwayName}` (lowercase snake_case), e.g. `boldly_tomato`,
 * `emilio_hotels_blush`. Composition is deterministic even though themeIds
 * themselves contain underscores — we always join, never split.
 */
export function colorwayEmbeddingKey(themeId: string, colorwayId: string): string {
  return `${themeId}_${colorwayId.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
}

/**
 * The swatch color is the first color value in a colorway array. Usually that
 * is index 0, but some templates put an image reference first — image values
 * are media-service filenames while colors are always `rgb(...)`/`rgba(...)`.
 */
const COLOR_VALUE_RE = /^rgba?\(/;
export function swatchColorFromColorway(values: string[] | undefined): string {
  return values?.find(value => COLOR_VALUE_RE.test(value)) ?? 'transparent';
}

/**
 * Colorway arrays carry no `--image`/`--color` name prefix to key off, so we
 * determine which indices hold image references by scanning the page's layers:
 * `var(--N)` on an image's `src` marks index N as an image.
 */
export function imageVarIndices(layers: ReadonlyArray<LayerData>): Set<number> {
  const out = new Set<number>();
  for (const layer of layers) {
    const src = layer.type === 'image' ? layer.imageData.src : undefined;
    const match = src?.match(VAR_RE);
    if (match) out.add(Number(match[1]));
  }
  return out;
}

/** Builds inline CSS custom properties (`--N`) for the selected colorway's values. */
export function buildCssVarStyle(values: string[], imageIndices: Set<number>, rendition?: Rendition): CSSProperties {
  const result: Record<string, string> = {};
  values.forEach((value, index) => {
    if (imageIndices.has(index)) {
      const fullUrl = absolutePhotoUrl(value);
      result[`--${index}`] = `url(${applyRenditionIfMediaServiceUrl(fullUrl, rendition)})`;
    } else {
      result[`--${index}`] = value;
    }
  });
  return result as CSSProperties;
}
